import { NavLink, useParams } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Package, Users, Menu } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface TabDef {
  id: string;
  to: (storeId: string) => string;
  matchPaths: (storeId: string) => string[];
  Icon: LucideIcon;
  labelKey: string;
  labelFallback: string;
}

const TABS: TabDef[] = [
  {
    id: 'home',
    to: (s) => `/${s}`,
    matchPaths: (s) => [`/${s}`, `/${s}/`],
    Icon: LayoutDashboard,
    labelKey: 'sidebar.home',
    labelFallback: 'Home',
  },
  {
    id: 'orders',
    to: (s) => `/${s}/orders`,
    matchPaths: (s) => [`/${s}/orders`],
    Icon: ShoppingCart,
    labelKey: 'sidebar.orders',
    labelFallback: 'Orders',
  },
  {
    id: 'products',
    to: (s) => `/${s}/products`,
    matchPaths: (s) => [`/${s}/products`],
    Icon: Package,
    labelKey: 'sidebar.products',
    labelFallback: 'Products',
  },
  {
    id: 'customers',
    to: (s) => `/${s}/customers`,
    matchPaths: (s) => [`/${s}/customers`],
    Icon: Users,
    labelKey: 'sidebar.customers',
    labelFallback: 'Customers',
  },
];

interface MobileTabBarProps {
  onMoreTap: () => void;
  moreActive: boolean;
}

// Mirrors Apple HIG: 5 tabs max, icon + short label, selected uses accent
// color, full-width at the bottom with safe-area padding so the tap target
// doesn't sit on top of the home indicator.
export function MobileTabBar({ onMoreTap, moreActive }: MobileTabBarProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();

  if (!storeId) return null;

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-bg-secondary border-t border-border-default"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Primary"
    >
      <ul className="flex items-stretch justify-around h-14">
        {TABS.map((tab) => {
          const to = tab.to(storeId);
          const Icon = tab.Icon;
          return (
            <li key={tab.id} className="flex-1">
              <NavLink
                to={to}
                end={tab.id === 'home'}
                className={({ isActive }) =>
                  clsx(
                    'h-full w-full flex flex-col items-center justify-center gap-0.5',
                    'text-[10px] font-medium select-none',
                    'transition-colors',
                    isActive && !moreActive ? 'text-accent' : 'text-text-muted',
                  )
                }
              >
                <Icon size={22} strokeWidth={2} />
                <span>{t(tab.labelKey, tab.labelFallback)}</span>
              </NavLink>
            </li>
          );
        })}
        <li className="flex-1">
          <button
            type="button"
            onClick={onMoreTap}
            className={clsx(
              'h-full w-full flex flex-col items-center justify-center gap-0.5',
              'text-[10px] font-medium select-none',
              'transition-colors',
              moreActive ? 'text-accent' : 'text-text-muted',
            )}
          >
            <Menu size={22} strokeWidth={2} />
            <span>{t('sidebar.more', 'More')}</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
